import { Select, Tooltip, Table, Pagination } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import stakingApi from 'api/stakingApi';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import LoadingSpinner from 'components/Commons/LoadingSpinner';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import { StakingStatus, UserStakingResponse } from 'models/stake';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FaEye, FaTimes } from 'react-icons/fa';
import { platformStates } from 'redux/platformSlice';
import { formatDateNoTime } from 'utils/dateUtils';
import { formatAmountV2 } from 'utils/formatAmount';
import StakingHistoryItem from './StakingHistoryItem';
import Typo from 'consts/typography';
import ClaimHistoryModal from './ClamHistoryModal';
import { showConfirmModal, showSuccessModal } from 'components/Modals/CommonModals';
import InfomationIcon from 'assets/icons/InfomationIcon';

interface StakingHistoryProps {}

const StakingHistory: React.FunctionComponent<StakingHistoryProps> = () => {
  const { isDesktop, isLaptop, isPhone, isTablet } = useAppSelector(platformStates);
  const t = useAppTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stakingHistory, setStakingHistory] = useState<UserStakingResponse[]>([]);
  const [totalHistory, setTotalHistory] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [status, setStatus] = useState<string>('');
  const { currentUser } = useUserDetail();
  const handleResponseError = useHandleResponseError();
  const [selectedItem, setSelectedItem] = useState<UserStakingResponse | null>(null);
  const [selectedItemCancel, setSelectedItemCancel] = useState<UserStakingResponse | null>(null);

  const STATUS_OPTION = [
    {
      value: '',
      label: <span className={`primary-text-color ${isPhone ? Typo.p6 : Typo.p4}`}>ALL</span>,
    },
    {
      value: 'STAKING',
      label: <span className={`primary-text-color ${isPhone ? Typo.p6 : Typo.p4}`}>STAKING</span>,
    },
    {
      value: 'CANCEL',
      label: <span className={`primary-text-color ${isPhone ? Typo.p6 : Typo.p4}`}>CANCEL</span>,
    },
  ];

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const { ok, body, error, pagination } = await stakingApi.getStakingList({
      sort: 'id,desc',
      size: 10,
      page: currentPage,
      'status.equals': status || null,
    });

    setIsLoading(false);

    if (ok && body) {
      setStakingHistory(
        body.map((t, index) => ({ ...t, key: t.id, stt: currentPage * 10 + index + 1 })),
      );
      setTotalHistory(pagination?.total || 0);
    }

    handleResponseError(error);
  }, [currentPage, status, currentUser?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCancelStaking = async (userStakingId: number) => {
    const { ok } = await stakingApi.cancelStaking(userStakingId);
    if (ok) {
      showSuccessModal({
        content: t('staking-history.success'),
        onOk: () => fetchData(),
      });
    }
  };

  const renderButtons = (row: UserStakingResponse) => {
    const showConfirmCancel = () => {
      if (row.status === StakingStatus.CANCEL) {
        return;
      }
      showConfirmModal({
        content: <p>{t('staking-history.cancel-title')}</p>,
        width: 360,
        icon: <InfomationIcon />,
        onOk: () => onCancelStaking(row.id),
        okText: t('sharebet.popup.button-ok'),
        cancelText: t('sharebet.popup.button-cancel'),
      });
    };

    return (
      <div className="flex-row staking-action items-center justify-center">
        <Tooltip
          placement={'bottom'}
          title={t('staking-history.header.claim-history')}
          color="#636363"
          showArrow={false}
        >
          <div className="referral-detail-btn" onClick={() => setSelectedItem(row)}>
            <FaEye />
          </div>
        </Tooltip>
        <Tooltip
          placement={'bottom'}
          title={t('staking-history.header.cancel')}
          color="#636363"
          showArrow={false}
        >
          <div
            className={`referral-detail-btn-cancel ${
              row.status === StakingStatus.CANCEL ? 'disabled' : ''
            }`}
            onClick={showConfirmCancel}
          >
            <FaTimes />
          </div>
        </Tooltip>
      </div>
    );
  };

  const tableColumns = useMemo((): ColumnsType<UserStakingResponse> => {
    return [
      {
        title: t('staking-history.header.id'),
        dataIndex: 'stt',
        align: 'center',
      },
      {
        title: t('staking-history.header.vault-name'),
        dataIndex: 'stakingVaultDto',
        render: (value) => <span>{value.label}</span>,
        width: 140,
      },
      {
        title: t('staking-history.header.day-number'),
        dataIndex: 'stakingVaultDto',
        render: (value) => <span>{value.dayNumber}</span>,
        width: 140,
        align: 'center',
      },
      {
        title: t('staking-history.header.reward-percent'),
        dataIndex: 'stakingVaultDto',
        render: (value) => <span>{value.rewardPercent}%</span>,
        width: 200,
        align: 'center',
      },
      {
        title: t('staking-history.header.stake-amount'),
        dataIndex: 'stakeAmount',
        render: (value) => <span>{formatAmountV2(value, 8)} $TBET</span>,
        width: 250,
        align: 'right',
      },
      {
        title: t('staking-history.header.status'),
        dataIndex: 'status',
        render: (value) => (
          <span className={value === StakingStatus.CANCEL ? 'staking-cancel' : 'staking-progress'}>
            {value}
          </span>
        ),
        width: 100,
        align: 'right',
      },
      {
        title: t('staking-history.header.next-claim-date'),
        dataIndex: 'claimDate',
        render: (_, record) => (
          <span>
            {record.status === StakingStatus.CANCEL ? '--' : formatDateNoTime(record.claimDate)}
          </span>
        ),
        width: 220,
        align: 'right',
      },
      {
        title: t('staking-history.header.action'),
        dataIndex: 'action',
        render: (value, record) => renderButtons(record),
        width: 180,
        align: 'center',
      },
    ];
  }, [stakingHistory.length]);

  const handleChangeStatus = (value: string) => {
    setCurrentPage(0);
    setStatus(value);
  };

  return (
    <Fragment>
      <div className="search-group gap-4">
        <div className="flex-row items-center gap-2">
          <span className="primary-text-color label">{`${t(
            'staking-history.header.status',
          )}: `}</span>
          <Select
            className="converter-dropdown primary-text-color"
            dropdownClassName="asset-dropdown primary-text-color"
            value={status}
            onChange={handleChangeStatus}
            options={STATUS_OPTION}
          />
        </div>
      </div>
      {isDesktop && !isLaptop ? (
        <Table
          className="common-table"
          columns={tableColumns}
          dataSource={stakingHistory}
          pagination={{
            position: ['bottomRight'],
            total: totalHistory,
            pageSize: 10,
            current: currentPage + 1,
            onChange: (page) => setCurrentPage(page - 1),
          }}
          scroll={{ x: 'max-content', ...(totalHistory !== 0 && { y: 500 }) }}
          bordered
          tableLayout="auto"
          loading={false}
        />
      ) : (
        <Fragment>
          {stakingHistory.length !== 0 ? (
            <Fragment>
              <div className="transaction-list cashier-list">
                {stakingHistory.map((item) => (
                  <StakingHistoryItem item={item} fetch={fetchData} />
                ))}
              </div>
              <div className="flex-row align-center justify-center mt-2">
                <Pagination
                  current={currentPage + 1}
                  total={totalHistory}
                  onChange={(page) => setCurrentPage(page - 1)}
                  pageSize={10}
                  className="common-pagination"
                />
              </div>
            </Fragment>
          ) : (
            <div className="no-data">
              <EmptyTableIcon textColor="white" />
            </div>
          )}
        </Fragment>
      )}
      {isLoading && (
        <div className="loading">
          <LoadingSpinner className="common__loading" />
        </div>
      )}
      {selectedItem && (
        <ClaimHistoryModal
          visible={!!selectedItem}
          item={selectedItem}
          onCancel={() => setSelectedItem(null)}
        />
      )}
    </Fragment>
  );
};
export default StakingHistory;
